import { t } from 'i18next';
import moment from 'moment';
import { useMemo, useState } from 'react';
import { Col } from '../../../../../../../../shared/frontend/components/grid/Col';
import { Row } from '../../../../../../../../shared/frontend/components/grid/Row';
import { Link } from '../../../../../../../../shared/frontend/components/link';
import { Stack } from '../../../../../../../../shared/frontend/components/stack/Stack';
import { TAction } from '../../../../../../../../shared/frontend/scripts/actions/types/action';
import {
  useAllClasses,
  useAllSpecs,
  useRandomSpecWithClass
} from '../../../../../../../../shared/frontend/scripts/selectors/global';
import { getSeasonBySlug } from '../../../../../../shared/enums';
import KeystoneActionCreator from '../../../../actions/keystoneActionCreator';
import { namedRoutes, resolveRoutePath } from '../../../../bundles/routes';
import { SpecSelect } from '../../../../../../../../shared/frontend/scripts/wow/components/controls/SpecSelect';
import { SpecRankingsTable } from '../../../../keystoneSpecRankings/components/SpecRankingsTable';
import {
  createServerPreloadActionsToPreloadSpecRankings,
  useKeystoneSpecRankingsData
} from '../../../../keystoneSpecRankings/hooks';
import { apiServiceAsync } from '../../../../services/apiService';
import { KeystoneRunRankingsTableFrontpage } from '../../../presentational/keystoneRunRankingsElements/KeystoneRunRankingsTableFrontpage';
import KeystoneRunRankingsContent from '../../keystoneRunRankingsContent';
import { createSpecParams, createTopMPlusRunsParams, getTopMPlusInstanceId } from './utils';
import { SeasonCountdownPane } from '../countdownPane';
import { HomePageSection } from '../ui/HomePageSection';
import { TSpecClassParams } from '../../homeContent';

export type TMythicPlusTopRunsPaneProps = {
  enabled: boolean;
  seasonSlug: string;
  region: string;
  realm?: string;
  topSpecParams?: TSpecClassParams;
};

export function MythicPlusTopRunsPane(props: TMythicPlusTopRunsPaneProps) {
  if (!props.enabled) {
    return null;
  }

  const season = getSeasonBySlug(props.seasonSlug);
  if (!season) {
    return null;
  }

  const isSeasonActive = moment.utc() >= season.starts.us;

  return (
    <>
      <div className="slds-m-bottom--small slds-m-top--large">
        <h2>
          <span className="rio-text-title--caps text-raiderio">{t('frontpage:section-header-top-mplus-runs')}</span>
          <span className="slds-m-left--small slds-text-title--caps text-white">
            {t(`seasons:${season.slug}.name`)} {season.affix && <>({t(`seasons:${season.slug}.affix`)})</>}
          </span>
        </h2>
      </div>

      {isSeasonActive ? (
        <Row gutter={['xx-small', 'xx-small']}>
          <Col size={12} md={6} grow className="slds-is-relative">
            <KeystoneRunRankingsContent
              instanceId={getTopMPlusInstanceId(props)}
              title={t('frontpage:top-timed-runs')}
              type="leaderboards-strict"
              component={KeystoneRunRankingsTableFrontpage}
              {...createTopMPlusRunsParams(props)}
              columns={{
                rank: true,
                time: false,
                tank: true,
                healer: true,
                dps: true,
                faction: false,
                dungeon: true,
                roleIcons: false,
                video: true,
                level: true
              }}
            />
          </Col>
          <Col size={12} md={6} grow className="slds-is-relative">
            <TopSpecs topSpecParams={props.topSpecParams} season={props.seasonSlug} region={props.region} />
          </Col>
        </Row>
      ) : (
        <SeasonCountdownPane season={season} index={1} />
      )}
    </>
  );
}

interface ITopSpecsProps {
  season: string;
  region: string;
  topSpecParams?: TSpecClassParams;
}

const TopSpecs = ({ season, region, topSpecParams }: ITopSpecsProps) => {
  const specs = useAllSpecs();
  const classes = useAllClasses();

  const [classSpecParams, setClassSpecParams] = useState<TSpecClassParams>(() => {
    if (topSpecParams) {
      return topSpecParams;
    }

    const { spec, cls } = useRandomSpecWithClass();

    return {
      classSlug: cls.slug,
      specSlug: spec.slug
    };
  });

  const currentSpec = useMemo(() => {
    const cls = classes.find((c) => c.slug === classSpecParams.classSlug)!;
    return specs.find((s) => s.class_id === cls.id && s.slug === classSpecParams.specSlug)!;
  }, [specs, classSpecParams]);

  const data = useKeystoneSpecRankingsData(
    createSpecParams({
      season,
      region,
      class: classSpecParams.classSlug,
      spec: classSpecParams.specSlug
    })
  );

  return (
    <HomePageSection
      title={t('frontpage:top-spec-rankings')}
      footer={
        <Link
          className="rio-realm-link--muted"
          to={resolveRoutePath(namedRoutes.KeystoneSpecRankings, {
            season,
            region,
            class: classSpecParams.classSlug,
            spec: classSpecParams.specSlug
          })}
        >
          {t('common:leaderboard.view-leaderboard-prompt')}
        </Link>
      }
      rightHeaderContent={
        <Stack justifyContent="flex-end">
          <SpecSelect
            value={currentSpec.id}
            getOptionValue={(option) => option.id}
            onChange={(__, spec, cls) => {
              setClassSpecParams({
                classSlug: cls.slug,
                specSlug: spec.slug
              });
            }}
            isClearable={false}
            size="xx-small"
            variant="borderless"
            isSearchable={false}
            viewOptions={{
              classColor: true,
              menuValue: {
                clsName: false,
                specName: 'short-name'
              }
            }}
            valueColor="raiderio"
            styles={{
              menu: (base) => ({
                ...base,
                minWidth: 200,
                position: 'absolute',
                right: 0
              })
            }}
          />
        </Stack>
      }
    >
      <SpecRankingsTable isLoading={data.isLoading} data={data} hideHeader />
    </HomePageSection>
  );
};

MythicPlusTopRunsPane.preloadComponentActions = async (props: TMythicPlusTopRunsPaneProps): Promise<TAction<any>[]> => {
  if (!props.enabled) {
    return [];
  }

  const actions: TAction<any>[] = [];

  const getRankingsActions = async (): Promise<TAction<any>[]> => {
    const loadParams = createTopMPlusRunsParams(props);
    const data = await apiServiceAsync.getKeystoneRunRankings(loadParams);
    return [KeystoneActionCreator.setKeystoneRankings(getTopMPlusInstanceId(props), data.rankings)];
  };

  const topSpecPromiseActions = props.topSpecParams
    ? (async () => {
        const specParams = createSpecParams({
          season: props.seasonSlug,
          region: props.region,
          class: props.topSpecParams!.classSlug,
          spec: props.topSpecParams!.specSlug
        });
        return createServerPreloadActionsToPreloadSpecRankings(specParams)();
      })()
    : Promise.resolve([]);

  const [rankingsActions, topSpecActions] = await Promise.all([getRankingsActions(), topSpecPromiseActions]);

  actions.push(...rankingsActions, ...topSpecActions);

  return actions;
};
